import { type ModalRegistration } from '@/shared/components/Modals/modal-registry/modal-registry.types';

import { ApproveCaseModal } from '../components/ApproveCaseModal/ApproveCaseModal';
import { RecordInterviewOutcomeModal } from '../components/RecordInterviewOutcomeModal/RecordInterviewOutcomeModal';
import { RejectCaseModal } from '../components/RejectCaseModal/RejectCaseModal';
import { RequestChangesModal } from '../components/RequestChangesModal/RequestChangesModal';
import { ScheduleInterviewModal } from '../components/ScheduleInterviewModal/ScheduleInterviewModal';
import { StepVerdictModal } from '../components/StepVerdictModal/StepVerdictModal';

/**
 * The module's dialog TYPES.
 *
 * Global strings, so they carry the module name: the registry throws on a
 * collision rather than letting whichever registered last silently win.
 */
export const VERIFICATION_MODALS = {
  APPROVE_CASE: 'verification.approve-case',
  REJECT_CASE: 'verification.reject-case',
  REQUEST_CHANGES: 'verification.request-changes',
  /** Refuse or skip ONE step. The verdict travels in the payload — see below. */
  STEP_VERDICT: 'verification.step-verdict',
  SCHEDULE_INTERVIEW: 'verification.schedule-interview',
  RECORD_INTERVIEW_OUTCOME: 'verification.record-interview-outcome',
} as const;

/**
 * The registrations `app.config.tsx` installs.
 *
 * ## Why approve and reject are two components and fail/waive are one
 *
 * Not a style choice — it follows what the dialogs actually contain.
 *
 * **Approve and reject differ structurally.** Approve carries a note and a
 * sentence about what the member gains; reject carries a required reason code
 * out of eight, a note, and a verbatim preview of what the member will read. One
 * component branching on an action would leave half its props inert on each
 * branch — the defect `ComboboxFieldDef`'s own doc records as the reason it is
 * not a flag on `select`.
 *
 * **Fail and waive are the same dialog.** Both name one step, both require one
 * reason, both submit through the same mutation with a different verdict. What
 * differs is the copy and *who may open it* — and the permission belongs on the
 * control that opens it (P10.6j), never on the dialog. Two components here would
 * be two files to keep identical.
 *
 * Payloads are ids and strings only: `ModalData` is serializable-only, and each
 * dialog reads the case from the cache the screen already filled.
 */
export const verificationModals: readonly ModalRegistration[] = [
  { modalTypes: [VERIFICATION_MODALS.APPROVE_CASE], component: ApproveCaseModal },
  { modalTypes: [VERIFICATION_MODALS.REJECT_CASE], component: RejectCaseModal },
  { modalTypes: [VERIFICATION_MODALS.REQUEST_CHANGES], component: RequestChangesModal },
  { modalTypes: [VERIFICATION_MODALS.STEP_VERDICT], component: StepVerdictModal },
  { modalTypes: [VERIFICATION_MODALS.SCHEDULE_INTERVIEW], component: ScheduleInterviewModal },
  {
    modalTypes: [VERIFICATION_MODALS.RECORD_INTERVIEW_OUTCOME],
    component: RecordInterviewOutcomeModal,
  },
];
